<template>
  <div class="pdl-detail-locations mt-5" v-if="pdl">

    <v-row v-if="canWrite">
      <v-col class="text-right">
        <v-btn small color="primary" @click="openPicker">
          <v-icon small left>mdi-link-variant-plus</v-icon>
          {{ $t('pdl.attach-locations') }}
        </v-btn>
      </v-col>
    </v-row>

    <v-data-table
        :headers="headers"
        :items="items"
        :loading="loading"
        hide-default-footer
        disable-pagination
    >
      <template #item.address="{value}">
        <template v-if="value">
          {{ value.street }} {{ value.number }}, {{ value.zipcode }} {{ value.city }}
        </template>
        <template v-else>-</template>
      </template>

      <template #item.actions="{item}">
        <v-btn icon small v-if="canDetach(item)" :title="$t('pdl.detach')" @click="detach(item)">
          <v-icon small>mdi-link-variant-off</v-icon>
        </v-btn>
      </template>
    </v-data-table>

    <v-dialog v-model="pickerDialog" max-width="1000">
      <v-card class="pdl-location-picker">
        <v-card-title>{{ $t('pdl.attach-locations') }}</v-card-title>
        <v-card-subtitle class="pb-0">{{ $t('pdl.attach-locations-subtitle') }}</v-card-subtitle>
        <v-card-text class="pt-6">
          <v-row>
            <v-col cols="12" md="5">
              <organization-select
                  :label="$t('organization.organization')"
                  v-model="pickerOrganizationId"
                  selection-with-no-extra
                  outlined
                  dense
                  hide-details
                  clearable
              />
            </v-col>
            <v-col cols="12" md="7">
              <v-text-field
                  :label="$t('search')"
                  v-model="pickerSearch"
                  prepend-inner-icon="mdi-magnify"
                  outlined
                  dense
                  clearable
                  hide-details
              />
            </v-col>
          </v-row>

          <v-divider class="mt-2 mb-4"></v-divider>

          <v-data-table
              v-model="pickerSelection"
              :headers="pickerHeaders"
              :items="pickerItems"
              :loading="pickerLoading"
              :options.sync="pickerOptions"
              :server-items-length="pickerTotal"
              :footer-props="{ 'items-per-page-options': [10, 20, 50] }"
              item-key="id"
              show-select
          >
            <template #item.organization.name="{item}">
              <template v-if="item.organization">{{ item.organization.name }}</template>
              <template v-else>-</template>
            </template>

            <template #item.count="{value}">
              {{ value || 0 }}
            </template>

            <template #item.address="{value}">
              <template v-if="value">
                {{ value.street }} {{ value.number }}, {{ value.zipcode }} {{ value.city }}
              </template>
              <template v-else>-</template>
            </template>
          </v-data-table>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-row class="ma-0">
            <v-col class="text-right">
              <v-btn class="mr-3" small @click="pickerDialog = false">{{ $t('cancel') }}</v-btn>
              <v-btn small color="primary" :disabled="pickerSelection.length === 0 || attaching" :loading="attaching" @click="attachSelection">
                {{ $t('pdl.attach') }}<template v-if="pickerSelection.length > 0"> ({{ pickerSelection.length }})</template>
              </v-btn>
            </v-col>
          </v-row>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </div>
</template>

<script>
import PdlRepository from "@repository/PdlRepository";
import LocationRepository from "@repository/LocationRepository";
import OrganizationSelect from "@blocks/Select/OrganizationSelect.vue";

export default {
  components: {OrganizationSelect},

  data() {
    return {
      items: [],
      loading: false,

      pickerDialog: false,
      pickerOrganizationId: null,
      pickerSearch: '',
      pickerOptions: null,
      pickerItems: [],
      pickerTotal: 0,
      pickerLoading: false,
      pickerSelection: [],
      pickerSearchTimeout: null,
      attaching: false,
    }
  },

  mounted() {
    this.loadData()
  },

  watch: {
    'pdl.id'() {
      this.loadData()
    },

    pickerOrganizationId() {
      this.loadPickerData()
    },

    pickerSearch() {
      clearTimeout(this.pickerSearchTimeout)
      this.pickerSearchTimeout = setTimeout(() => {
        if (this.pickerOptions) {
          this.pickerOptions.page = 1
        }
        this.loadPickerData()
      }, 300)
    },

    pickerOptions() {
      this.loadPickerData()
    },
  },

  computed: {
    pdl() {
      return this.$parent.pdl ?? null
    },

    canWrite() {
      return this.currentUser.isZE && this.currentUser.permissions.includes('STATION_WRITE')
    },

    headers() {
      const headers = [
        { text: this.$t('name'), value: 'name', sortable: false },
        { text: this.$t('organization.organization'), value: 'organizationName', sortable: false },
        { text: this.$tc('location.location'), value: 'address', sortable: false },
      ]
      if (this.canWrite) {
        headers.push({ value: 'actions', align: 'right', sortable: false })
      }
      return headers
    },

    pickerHeaders() {
      return [
        { text: this.$t('name'), value: 'name' },
        { text: this.$t('organization.organization'), value: 'organization.name', sortable: false },
        { text: this.$t('location.stations-count'), value: 'count', sortable: false },
        { text: this.$tc('location.location'), value: 'address', sortable: false },
      ]
    },
  },

  methods: {
    loadData() {
      if (!this.pdl?.id) return
      this.loading = true
      PdlRepository.locations(this.pdl.id).then(items => {
        this.items = items
        this.loading = false
      }).catch(e => {
        this.loading = false
        this.notifyError(e)
      })
    },

    openPicker() {
      this.pickerOrganizationId = this.pdl.organizationId
      this.pickerSearch = ''
      this.pickerSelection = []
      this.pickerDialog = true
      this.loadPickerData()
    },

    loadPickerData() {
      if (!this.pickerDialog) return
      this.pickerLoading = true

      let data = {
        all: true,
        search: this.pickerSearch,
        excludeOwnerType: 'HOME',
      }
      if (this.pickerOrganizationId) {
        data.organizationId = this.pickerOrganizationId
      }
      if (this.pickerOptions?.itemsPerPage > -1) {
        data.offset = (this.pickerOptions.page * this.pickerOptions.itemsPerPage) - this.pickerOptions.itemsPerPage
        data.limit = this.pickerOptions.itemsPerPage
      }
      if (this.pickerOptions?.sortBy?.length > 0) {
        data.orderBy = this.pickerOptions.sortBy[0]
        data.orderDirection = this.pickerOptions.sortDesc?.[0] ? 'desc' : 'asc'
      }

      LocationRepository.search(data).then(result => {
        const attachedIds = this.items.map(item => item.id)
        this.pickerItems = result.items.filter(item => !attachedIds.includes(item.id))
        this.pickerTotal = result.total
        this.pickerLoading = false
      }).catch(e => {
        this.pickerLoading = false
        this.notifyError(e)
      })
    },

    attachSelection() {
      this.attaching = true
      const queue = [...this.pickerSelection]
      const next = () => {
        const location = queue.shift()
        if (!location) {
          this.attaching = false
          this.pickerDialog = false
          this.loadData()
          return
        }
        PdlRepository.assignToLocation(location.id, this.pdl.id).then(next).catch(e => {
          this.attaching = false
          this.notifyError(e)
          this.loadData()
        })
      }
      next()
    },

    canDetach(item) {
      // Detaching falls back to the organization's DEFAULT Pdl: pointless when this Pdl already is that
      // default, and impossible without an organization to fall back to.
      if (!this.canWrite || !item.organizationId) return false
      return !(this.pdl.isDefault && item.organizationId === this.pdl.organizationId)
    },

    detach(item) {
      this.$dialog.confirm({
        text: this.$t('pdl.detach-confirm'),
      }).then(dialog => {
        if (dialog) {
          PdlRepository.assignToLocation(item.id, null).then(() => {
            this.loadData()
          }).catch(e => {
            this.notifyError(e)
          })
        }
      })
    },
  }
}
</script>


